/**
 * 登录后跳转工具函数
 * 统一处理 SSO、钉钉等登录后的跳转逻辑
 */

import { NavigateFunction } from 'react-router-dom';
import { generatePath as reactRouterGeneratePath } from '@/routes';

/**
 * 登录后跳转参数接口
 */
export interface LoginRedirectParams {
  /** URL 搜索参数 */
  searchParams: URLSearchParams;
  /** React Router 的 navigate 函数（仅前台跳转需要） */
  navigate?: NavigateFunction;
  /** 是否在钉钉环境中 */
  isDingTalk?: boolean;
  /** 管理后台的 origin（默认使用当前 origin） */
  adminOrigin?: string;
}

/**
 * 解析动态路由参数
 * 例如：path = '/ai-training/scenarios/:id/edit', params = { id: '123' }
 * 返回：'/ai-training/scenarios/123/edit'
 */
export function parseRoutePath(path: string, params?: Record<string, string | number>): string {
  if (!params || Object.keys(params).length === 0) {
    return path;
  }

  let result = path;
  
  // 替换所有 :paramName 为实际值
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`:${key}`, String(value));
  });

  return result;
}

/**
 * 构建管理后台跳转 URL
 * 
 * @param redirect - 重定向路径（可以是完整路径或路由名称）
 * @param routeParams - 路由参数（当 redirect 包含动态参数时使用，如 :id）
 * @param queryParams - 查询参数（URL 查询字符串参数，如 ?id=123&tab=detail）
 * @param isDingTalk - 是否在钉钉环境
 * @param adminOrigin - 管理后台的 origin
 * @returns 完整的管理后台 URL
 * 
 * @example
 * // 示例1: 跳转到固定页面
 * buildAdminUrl('/system/user', undefined, undefined, false)
 * // => 'http://localhost:3000/admin/system/user'
 * 
 * @example
 * // 示例2: 跳转到动态路由页面
 * buildAdminUrl('/ai-training/scenarios/:id/edit', { id: '123' }, undefined, false)
 * // => 'http://localhost:3000/admin/ai-training/scenarios/123/edit'
 * 
 * @example
 * // 示例3: 带查询参数的跳转
 * buildAdminUrl('/system/user', undefined, { id: '123', tab: 'detail' }, false)
 * // => 'http://localhost:3000/admin/system/user?id=123&tab=detail'
 * 
 * @example
 * // 示例4: 同时包含路径参数和查询参数
 * buildAdminUrl('/ai-training/scenarios/:id/edit', { id: '456' }, { tab: 'basic', mode: 'edit' }, false)
 * // => 'http://localhost:3000/admin/ai-training/scenarios/456/edit?tab=basic&mode=edit'
 * 
 * @example
 * // 示例5: redirect 中已包含查询参数
 * buildAdminUrl('/system/user?id=123&aa=456', undefined, undefined, false)
 * // => 'http://localhost:3000/admin/system/user?id=123&aa=456'
 * 
 * @example
 * // 示例6: 钉钉环境跳转
 * buildAdminUrl('/system/user', undefined, { id: '123' }, true)
 * // => 'http://localhost:3000/admin/system/user?id=123&ddtab=true'
 */
export function buildAdminUrl(
  redirect: string | undefined,
  routeParams: Record<string, string | number> | undefined,
  queryParams: Record<string, string | number> | undefined,
  isDingTalk: boolean,
  adminOrigin?: string
): string {
  const currentOrigin = adminOrigin || window.location.origin;
  let adminUrl = `${currentOrigin}/admin/`;

  if (redirect) {
    // 移除开头的斜杠（如果有）
    const normalizedPath = redirect.startsWith('/') ? redirect.slice(1) : redirect;
    
    // 解析动态路由参数（路径参数）
    const parsedPath = parseRoutePath(normalizedPath, routeParams);
    
    adminUrl += parsedPath;
  }

  // 添加查询参数
  if (queryParams && Object.keys(queryParams).length > 0) {
    const separator = adminUrl.includes('?') ? '&' : '?';
    const queryString = new URLSearchParams(
      Object.entries(queryParams).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    
    adminUrl += separator + queryString;
  }

  // 如果在钉钉环境下，添加 ddtab=true 参数
  if (isDingTalk) {
    adminUrl += adminUrl.includes('?') ? '&ddtab=true' : '?ddtab=true';
  }

  return adminUrl;
}

/**
 * 执行登录后跳转
 * 
 * @param params - 跳转参数
 * 
 * @description
 * 支持的 URL 参数：
 * - target: 'admin' 表示跳转到管理后台，其他或空表示前台
 * - redirect: 重定向路径（支持完整路径，可以包含查询参数）
 * - routeName: 路由名称（前台使用，推荐）
 * - routeParams: 路由参数（JSON 字符串格式，用于路径参数如 :id）
 * - queryParams: 查询参数（JSON 字符串格式，用于 URL 查询参数如 ?id=123）
 * 
 * @example
 * // 示例1: 前台路由名称跳转
 * // URL: /sso-login?code=xxx&routeName=taskDetail&routeParams={"id":"123"}
 * handleLoginRedirect({ searchParams, navigate })
 * 
 * @example
 * // 示例2: 后台固定页面跳转
 * // URL: /sso-login?code=xxx&target=admin&redirect=/system/user
 * handleLoginRedirect({ searchParams, isDingTalk: false })
 * 
 * @example
 * // 示例3: 后台动态路由跳转（路径参数）
 * // URL: /sso-login?code=xxx&target=admin&redirect=/ai-training/scenarios/:id/edit&routeParams={"id":"123"}
 * handleLoginRedirect({ searchParams, isDingTalk: false })
 * // 将跳转到: /admin/ai-training/scenarios/123/edit
 * 
 * @example
 * // 示例4: 后台跳转带查询参数
 * // URL: /dingtalk-login?target=admin&redirect=/system/user&queryParams={"id":"123","tab":"detail"}
 * handleLoginRedirect({ searchParams, isDingTalk: false })
 * // 将跳转到: /admin/system/user?id=123&tab=detail
 * 
 * @example
 * // 示例5: redirect 中直接包含查询参数
 * // URL: /sso-login?target=admin&redirect=/system/user?id=123&aa=456
 * handleLoginRedirect({ searchParams, isDingTalk: false })
 * // 将跳转到: /admin/system/user?id=123&aa=456
 * 
 * @example
 * // 示例6: 同时包含路径参数和查询参数
 * // URL: /sso-login?target=admin&redirect=/scenarios/:id/edit&routeParams={"id":"456"}&queryParams={"tab":"basic"}
 * handleLoginRedirect({ searchParams, isDingTalk: false })
 * // 将跳转到: /admin/scenarios/456/edit?tab=basic
 */
export function handleLoginRedirect(params: LoginRedirectParams): void {
  const { searchParams, navigate, isDingTalk = false, adminOrigin } = params;
console.log('开始解析跳转参数，searchParams=====>', searchParams);

  // 获取跳转参数
  const target = searchParams.get('target');
  const redirect = searchParams.get('redirect');
  const routeName = searchParams.get('routeName');
  const routeParamsStr = searchParams.get('routeParams');
  const queryParamsStr = searchParams.get('queryParams');

  // 收集所有额外的查询参数（不在预定义参数列表中的）
  const predefinedParams = ['target', 'redirect', 'routeName', 'routeParams', 'queryParams', 'code', 'state'];
  const extraParams: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (!predefinedParams.includes(key)) {
      extraParams[key] = value;
    }
  });

  console.log('🔄 登录后跳转参数 =>', { 
    target, 
    redirect, 
    routeName, 
    routeParamsStr, 
    queryParamsStr,
    extraParams: Object.keys(extraParams).length > 0 ? extraParams : undefined
  });

  // 解析路由参数（路径参数）
  let routeParams: Record<string, string | number> | undefined;
  if (routeParamsStr) {
    try {
      routeParams = JSON.parse(routeParamsStr);
    } catch (error) {
      console.error('❌ 解析路由参数失败 =>', error);
    }
  }

  // 解析查询参数
  let queryParams: Record<string, string | number> | undefined;
  if (queryParamsStr) {
    try {
      queryParams = JSON.parse(queryParamsStr);
    } catch (error) {
      console.error('❌ 解析查询参数失败 =>', error);
    }
  }

  // 合并额外参数到 queryParams 中
  if (Object.keys(extraParams).length > 0) {
    queryParams = { ...extraParams, ...queryParams };
    console.log('✅ 合并额外查询参数后 =>', queryParams);
  }

  // 判断是否跳转到管理后台
  if (target === 'admin') {
    // 构建管理后台 URL
    const adminUrl = buildAdminUrl(redirect || undefined, routeParams, queryParams, isDingTalk, adminOrigin);
    
    console.log('🔄 跳转到管理后台 =>', adminUrl);
    window.location.href = adminUrl;
  } else {
    // 跳转到前台
    if (!navigate) {
      console.error('❌ 前台跳转需要提供 navigate 函数');
      return;
    }

    let frontendPath = '/';

    // 优先使用 routeName + routeParams 的方式（推荐）
    if (routeName) {
      try {
        frontendPath = reactRouterGeneratePath(routeName, routeParams);
        console.log('✅ 使用路由名称跳转 =>', { routeName, routeParams, path: frontendPath });
      } catch (error) {
        console.error('❌ 解析路由名称失败，使用 redirect 或默认路径 =>', error);
        frontendPath = redirect || '/';
      }
    } else if (redirect) {
      // 使用 redirect 路径（也支持动态参数）
      frontendPath = parseRoutePath(redirect, routeParams);
      console.log('✅ 使用 redirect 路径跳转 =>', { redirect, routeParams, path: frontendPath });
    }

    // 添加查询参数到前台路径
    if (queryParams && Object.keys(queryParams).length > 0) {
      const separator = frontendPath.includes('?') ? '&' : '?';
      const queryString = new URLSearchParams(
        Object.entries(queryParams).reduce((acc, [key, value]) => {
          acc[key] = String(value);
          return acc;
        }, {} as Record<string, string>)
      ).toString();
      
      frontendPath += separator + queryString;
    }

    console.log('🔄 跳转到前台页面 =>', frontendPath);
    navigate(frontendPath, { replace: true });
  }
}

/**
 * 构建登录跳转 URL（用于生成登录链接）
 * 
 * @param loginPath - 登录页面路径（如 '/sso-login' 或 '/dingtalk-login'）
 * @param options - 跳转选项
 * @returns 完整的登录跳转 URL
 * 
 * @example
 * // 示例1: 前台路由名称跳转
 * buildLoginUrl('/sso-login', { 
 *   routeName: 'taskDetail', 
 *   routeParams: { id: '123' } 
 * })
 * // => '/sso-login?routeName=taskDetail&routeParams={"id":"123"}'
 * 
 * @example
 * // 示例2: 后台动态路由跳转（路径参数）
 * buildLoginUrl('/dingtalk-login', { 
 *   target: 'admin',
 *   redirect: '/ai-training/scenarios/:id/edit',
 *   routeParams: { id: '456' }
 * })
 * // => '/dingtalk-login?target=admin&redirect=/ai-training/scenarios/:id/edit&routeParams={"id":"456"}'
 * 
 * @example
 * // 示例3: 后台跳转带查询参数
 * buildLoginUrl('/sso-login', { 
 *   target: 'admin',
 *   redirect: '/system/user',
 *   queryParams: { id: '123', tab: 'detail' }
 * })
 * // => '/sso-login?target=admin&redirect=/system/user&queryParams={"id":"123","tab":"detail"}'
 * 
 * @example
 * // 示例4: redirect 中直接包含查询参数
 * buildLoginUrl('/dingtalk-login', { 
 *   target: 'admin',
 *   redirect: '/system/user?id=123&aa=456'
 * })
 * // => '/dingtalk-login?target=admin&redirect=/system/user?id=123&aa=456'
 */
export function buildLoginUrl(
  loginPath: string,
  options: {
    target?: 'admin' | 'frontend';
    redirect?: string;
    routeName?: string;
    routeParams?: Record<string, string | number>;
    queryParams?: Record<string, string | number>;
    [key: string]: any;
  }
): string {
  const params = new URLSearchParams();

  if (options.target) {
    params.append('target', options.target);
  }

  if (options.redirect) {
    params.append('redirect', options.redirect);
  }

  if (options.routeName) {
    params.append('routeName', options.routeName);
  }

  if (options.routeParams) {
    params.append('routeParams', JSON.stringify(options.routeParams));
  }

  if (options.queryParams) {
    params.append('queryParams', JSON.stringify(options.queryParams));
  }

  // 支持其他额外参数（如 corpId 等）
  Object.entries(options).forEach(([key, value]) => {
    if (!['target', 'redirect', 'routeName', 'routeParams', 'queryParams'].includes(key) && value !== undefined) {
      params.append(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${loginPath}?${queryString}` : loginPath;
}

